'use client';

import { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { CURRENT_PHOTO_CONSENT_VERSION, type PhotoConsentInput } from '@/lib/race-photos/consent';

interface ThemeColors {
  primary: string;
  secondary: string;
  text: string;
  textMuted: string;
  buttonText?: string;
}

interface PhotoConsentGateProps {
  themeColors: ThemeColors;
  onConsent: (payload: PhotoConsentInput) => void;
}

type AgeBand = 'over_18' | 'under_18';

/**
 * Consent gate shown before the camera opens. Facial recognition is special-
 * category biometric data, so we take (and record) explicit consent first. An
 * adult consents for themselves; someone under 18 needs a parent or guardian to
 * consent and give their name. The camera never starts until this is satisfied.
 */
export function PhotoConsentGate({ themeColors, onConsent }: PhotoConsentGateProps) {
  const [ageBand, setAgeBand] = useState<AgeBand | null>(null);
  const [selfConsent, setSelfConsent] = useState(false);
  const [guardianName, setGuardianName] = useState('');
  const [guardianConsent, setGuardianConsent] = useState(false);

  const valid =
    ageBand === 'over_18'
      ? selfConsent
      : ageBand === 'under_18'
        ? guardianConsent && guardianName.trim().length >= 2
        : false;

  const handleContinue = () => {
    if (!valid || !ageBand) return;
    onConsent({
      ageBand,
      selfConsent: ageBand === 'over_18' ? true : undefined,
      guardianName: ageBand === 'under_18' ? guardianName.trim() : undefined,
      guardianConsent: ageBand === 'under_18' ? true : undefined,
      consentVersion: CURRENT_PHOTO_CONSENT_VERSION,
    });
  };

  const ageButton = (band: AgeBand, label: string) => {
    const active = ageBand === band;
    return (
      <button
        type="button"
        onClick={() => setAgeBand(band)}
        style={{
          flex: 1,
          minHeight: '48px',
          padding: '12px 16px',
          borderRadius: '10px',
          border: active ? `2px solid ${themeColors.secondary}` : '1px solid var(--fv-border)',
          background: active ? 'rgba(0,120,112,0.08)' : 'var(--fv-bg-input)',
          color: 'var(--fv-text-primary)',
          fontSize: '15px',
          fontWeight: active ? 700 : 500,
          cursor: 'pointer',
        }}
      >
        {label}
      </button>
    );
  };

  const checkboxRow = (
    checked: boolean,
    onChange: (v: boolean) => void,
    label: string,
  ) => (
    <label
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        textAlign: 'left',
        cursor: 'pointer',
        fontSize: '14px',
        lineHeight: 1.5,
        color: 'var(--fv-text-primary)',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: '20px', height: '20px', marginTop: '1px', flex: '0 0 auto', accentColor: themeColors.secondary }}
      />
      <span>{label}</span>
    </label>
  );

  return (
    <div style={{ textAlign: 'center', maxWidth: '440px', margin: '0 auto' }}>
      <ShieldCheck size={36} style={{ color: themeColors.secondary, margin: '0 auto 16px' }} />
      <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--fv-text-primary)', margin: '0 0 8px' }}>
        Before we find your photos
      </h2>
      <p style={{ fontSize: '14px', color: 'var(--fv-text-secondary)', margin: '0 0 20px' }}>
        We use facial recognition to match your face with the event photos. Your selfie is
        deleted within 24 hours and your face data once the event photos are no longer available
        (and within 12 months at the latest).
      </p>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        {ageButton('over_18', "I'm 18 or over")}
        {ageButton('under_18', "I'm under 18")}
      </div>

      {ageBand === 'over_18' && (
        <div style={{ marginBottom: '20px' }}>
          {checkboxRow(
            selfConsent,
            setSelfConsent,
            'I consent to facial recognition being used to find my photos from this event.',
          )}
        </div>
      )}

      {ageBand === 'under_18' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' }}>
          <p style={{ fontSize: '13px', color: 'var(--fv-text-secondary)', margin: 0, textAlign: 'left' }}>
            A parent or guardian needs to give permission and complete this for you.
          </p>
          <input
            type="text"
            value={guardianName}
            onChange={(e) => setGuardianName(e.target.value)}
            placeholder="Parent or guardian full name"
            style={{
              width: '100%',
              padding: '14px 16px',
              borderRadius: '10px',
              border: '1px solid var(--fv-border)',
              background: 'var(--fv-bg-input)',
              color: 'var(--fv-text-primary)',
              fontSize: '16px',
              boxSizing: 'border-box',
            }}
          />
          {checkboxRow(
            guardianConsent,
            setGuardianConsent,
            'I am this person’s parent or guardian and I consent to facial recognition being used to find their photos from this event.',
          )}
        </div>
      )}

      <button
        type="button"
        onClick={handleContinue}
        disabled={!valid}
        style={{
          width: '100%',
          padding: '14px 32px',
          borderRadius: '10px',
          background: themeColors.secondary,
          color: themeColors.buttonText || '#fff',
          border: 'none',
          fontSize: '15px',
          fontWeight: 700,
          cursor: valid ? 'pointer' : 'not-allowed',
          opacity: valid ? 1 : 0.5,
          minHeight: '48px',
          transition: 'opacity 0.2s',
        }}
      >
        Continue to camera
      </button>
    </div>
  );
}
